import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { Alert, Button, Icon, List, TextInput } from '@pella/sash-framework';
import { CTA, LoadingOverlay } from '../../../';
import CustomerShowroomText from '../../../template/branches/customer-showroom-text';
import { isValidPostal } from '../../../utilities/validations';
import { useLocation } from '../../../../context/LocationContext';

const LocationButton = ({ className }) => {
	const locationDiv = useRef(null);
	const CSdata = useStaticQuery(query);

	const { userZip, selectedShowroom, loading, error, openLocation, changeLocation, locationList, geolocationZip, updateState, updateLocation, fetchShowroomsByZip, getCurrentLocation, showrooms, oldShowrooms, newShowrooms } = useLocation();

	const getShowroomData = (store) => ({
		...store,
		heading: newShowrooms?.nodes?.find((s) => s.showroom_id === store.store_number)?.page_heading?.replace(/<\/?span[^>]*>/g, ''),
		url: newShowrooms?.nodes?.find((s) => s.showroom_id === store.store_number)?.url || oldShowrooms?.nodes?.find((s) => s.showroom_id === store.store_number)?.url,
	});

	const mapAddress = `${selectedShowroom?.line_1}${selectedShowroom?.line_2 != null ? '+' + selectedShowroom?.line_2 : ''}+${selectedShowroom?.city},+${selectedShowroom?.state}+${selectedShowroom?.postal_code}`;

	const outsideLocationClick = useCallback(
		(e) => {
			if (e.target && locationDiv?.current && !locationDiv.current.contains(e.target)) {
				updateState({ openLocation: false });
			}
		},
		[updateState]
	);

	useEffect(() => {
		document.addEventListener('mousedown', outsideLocationClick);
		return () => document.removeEventListener('mousedown', outsideLocationClick);
	}, [outsideLocationClick]);

	const onPostalFormSubmit = async (e) => {
		e.preventDefault();
		if (!isValidPostal(userZip)) {
			updateState({ error: 'Please enter a valid postal code' });
			return;
		}
		updateState({ error: null, loading: true });
		fetchShowroomsByZip(userZip);
	};

	const onOpenLocationClick = () => {
		updateState({
			openLocation: !openLocation,
			locationList: [],
		});
	};

	const onChangeLocationClick = () => {
		updateState({ changeLocation: true });
		setTimeout(() => document.querySelector('#zipCodeLookup')?.focus(), 100);
	};

	const onLocationItemClick = (store, e) => {
		e.preventDefault();
		const zip = isValidPostal(userZip) ? userZip : store.postal_code;
		updateLocation(store, zip);
		updateState({
			changeLocation: false,
			userZip: '',
		});
	};

	const onInputChange = (e, value) => {
		e.preventDefault();
		updateState({
			error: null,
			locationList: [],
			userZip: value,
		});
	};

	const onGeoLocationIconClick = (e) => {
		e.preventDefault();
		updateState({ loading: true });

		if (geolocationZip?.saved) {
			updateState({ userZip: geolocationZip.saved });
			fetchShowroomsByZip(geolocationZip.saved);
			return;
		}

		getCurrentLocation();
	};

	const getMatchingShowrooms = (zip) => {
		if (!Array.isArray(showrooms) || !zip) return [];
		return showrooms.filter((showroom) => showroom?.postal_code?.startsWith(zip));
	};

	const shouldShowGeolocationButton = () => {
		if (!geolocationZip?.enabled) return false;
		if (userZip?.length < 3) return true;
		return userZip?.length >= 3 && getMatchingShowrooms(userZip).length === 0;
	};

	const shouldShowPostalSuggestions = () => {
		const hasMatches = getMatchingShowrooms(userZip).length > 0;
		return hasMatches && userZip?.length > 2 && !isValidPostal(userZip);
	};

	return (
		<div
			ref={locationDiv}
			className={`locationBtn-wrapper group font-normal max-xl:border-b max-xl:border-b-neutral-200 ${className || ''}`}
		>
			<button
				onClick={onOpenLocationClick}
				className='locationBtn flex w-full items-center border-b border-b-neutral-200 p-2.5 text-sm xl:border-0 xl:p-0'
			>
				<Icon
					name='location_on'
					className='mr-1'
					size={20}
				/>
				<span className={`font-semibold ${!selectedShowroom?.city ? 'uppercase tracking-wider underline decoration-primary decoration-2 underline-offset-4 group-focus-within:no-underline group-hover:no-underline' : ''}`}>{selectedShowroom?.city ? `${selectedShowroom.city}, ${selectedShowroom.state}` : CSdata.contentstackLocationConfig?.select_location?.heading}</span>
			</button>

			<div className={`locationOptions before:absolute before:-top-[23px] before:right-8 before:z-3 before:border-[12px] before:border-transparent before:border-b-white after:absolute after:-top-6 after:right-8 after:z-2 after:border-[12px] after:border-transparent after:border-b-neutral-400 xl:absolute xl:-right-5 xl:top-full xl:mt-4 xl:bg-white xl:shadow-lg ${openLocation ? 'block' : 'hidden'}`}>
				<div className='locationOptions-box relative p-4 xl:min-h-[200px] xl:border xl:border-neutral-400 xl:px-6'>
					<div className={`locationOptions-form ${changeLocation ? 'block xl:w-[375px]' : 'hidden'}`}>
						<p className='mb-2 text-base font-semibold'>{CSdata.contentstackLocationConfig?.select_location?.heading}</p>

						<div
							dangerouslySetInnerHTML={{ __html: CSdata.contentstackLocationConfig.global_location.description }}
							className='text-sm leading-tight text-neutral-600'
						/>

						<form
							id='zipCodeForm'
							autoComplete='off'
							className='mt-4 flex items-end gap-x-1'
							onSubmit={onPostalFormSubmit}
						>
							<div className='relative flex-1'>
								<TextInput
									id='zipCodeLookup'
									type='text'
									labelText={CSdata.contentstackLocationConfig.global_location.box_heading}
									placeholder='Enter Zip Code'
									onChange={(e) => onInputChange(e, e.target.value)}
									groupClass='!mb-0'
									className='!min-h-[48px] !py-2.5'
									maxLength={7}
									value={userZip}
									autoComplete='postal-code'
								/>

								{shouldShowGeolocationButton() && (
									<button
										type='button'
										className='absolute bottom-0 right-0 flex h-12 items-center justify-center px-2'
										onClick={onGeoLocationIconClick}
										aria-label='Use geolocation'
									>
										<Icon name='location_on' />
									</button>
								)}

								{shouldShowPostalSuggestions() && (
									<List
										bullets='none'
										className='absolute left-0 top-full w-full border border-t-0 border-black bg-white py-2'
									>
										{geolocationZip?.enabled && (
											<li>
												<button
													type='button'
													className='flex w-full items-center bg-neutral-50 p-2 text-xs'
													onClick={(e) => onInputChange(e, geolocationZip.saved || userZip)}
													aria-label='Use current location'
												>
													<Icon
														name='location_on'
														size={16}
														className='mr-1'
													/>
													<span>
														{CSdata.contentstackLocationConfig?.select_location?.current_location}
														{(geolocationZip.saved || userZip) && <span className='before:content-[",_"]'>{geolocationZip.saved || userZip}</span>}
													</span>
												</button>
											</li>
										)}

										{showrooms
											.filter((showroom) => showroom.postal_code.startsWith(userZip))
											.map((showroom, index) => (
												<li key={index}>
													<button
														className='w-full py-2 pl-3 text-left text-xs hover:bg-neutral-50 focus:bg-neutral-50'
														onClick={(e) => {
															onInputChange(e, showroom.postal_code);
															fetchShowroomsByZip(showroom.postal_code);
														}}
													>
														<span
															className='mr-1 [&>span]:font-bold'
															dangerouslySetInnerHTML={{ __html: showroom.postal_code.replace(userZip, '<span>' + userZip + '</span>') }}
														/>
														<span>
															{showroom.city}, {showroom.state}
														</span>
													</button>
												</li>
											))}
									</List>
								)}
							</div>

							<Button
								type='submit'
								color='primary-alt'
								className='locationOptions-formSubmit h-12 !w-auto flex-none'
								size='sm'
							>
								Submit
							</Button>
						</form>

						{error && (
							<Alert
								type='info'
								className='mt-2 items-center !p-3'
								hideClose
							>
								<p className='text-sm'>{error}</p>
							</Alert>
						)}

						{loading && (
							<LoadingOverlay
								fixed={false}
								svgSize={200}
							/>
						)}

						{!loading && locationList?.[0] && (
							<div className='locationOptions-locationList mt-4 max-h-[300px] overflow-auto'>
								<List
									bullets='none'
									className='divide-y-neutral-100 divide-y border border-neutral-100'
								>
									{locationList.map((store, index) => {
										if (!store) return null;

										const showroomData = getShowroomData(store);

										return (
											<li key={index}>
												<button
													type='button'
													onClick={(el) => onLocationItemClick(showroomData, el)}
													className='locationOptions-location block w-full bg-white p-3 text-left font-normal hover:bg-neutral-25 focus:bg-neutral-25'
												>
													<p className='text-base font-semibold uppercase leading-snug'>{showroomData.heading || showroomData.name}</p>

													<div className='mt-2 flex'>
														{showroomData.dist?.miles?.value && (
															<span className='flex-1 text-xs'>
																{showroomData.dist.miles.value} {showroomData.dist.miles.unit}
															</span>
														)}

														<span className='flex-1 text-right text-sm text-neutral-600'>
															{showroomData.city}, {showroomData.state}
														</span>
													</div>
												</button>
											</li>
										);
									})}
								</List>
							</div>
						)}

						{CSdata.contentstackLocationConfig?.select_location?.cta?.[1] && (
							<CTA
								data={CSdata.contentstackLocationConfig.select_location.cta[1]}
								className='mt-4'
							/>
						)}
					</div>

					<div className={`locationOptions-selected ${!changeLocation ? 'block xl:w-[325px]' : 'hidden'}`}>
						<p className='text-base font-semibold uppercase leading-snug'>
							{selectedShowroom?.url && (
								<Link
									className='decoration-transparent hover:decoration-primary focus:decoration-primary'
									to={selectedShowroom.url}
								>
									{selectedShowroom.heading || selectedShowroom.name}
								</Link>
							)}
						</p>

						<p className='mb-1 mt-0 text-sm text-neutral-600'>
							{CSdata.contentstackLocationConfig?.select_location?.based_on}, {userZip}
						</p>

						{selectedShowroom?.branch_type && (
							<CustomerShowroomText
								className='my-3'
								showroomData={selectedShowroom}
								showInfo={false}
							/>
						)}

						<p className='mb-4 mt-0'>
							<button
								onClick={onChangeLocationClick}
								className='text-sm font-semibold uppercase tracking-wider underline decoration-2 underline-offset-2 hover:no-underline focus:no-underline'
							>
								{CSdata.contentstackLocationConfig?.select_location?.change_location}
							</button>
						</p>

						<List
							bullets='none'
							className='space-y-2'
						>
							{mapAddress && (
								<li>
									<Button
										href={`https://www.google.com/maps/place/${mapAddress.replace(/\s+/g, '+')}`}
										target='_blank'
										rel='noopener noreferrer'
										title='Link will open google maps in a new tab'
										color='secondary'
										size='sm'
										wide
									>
										{CSdata.contentstackLocationConfig?.select_location?.get_directions}
									</Button>
								</li>
							)}
							{CSdata.contentstackLocationConfig?.select_location?.cta?.[0] && (
								<li>
									<CTA
										data={CSdata.contentstackLocationConfig.select_location.cta[0]}
										size='sm'
										wide
									/>
								</li>
							)}
						</List>

						<div className='mt-5 border-t border-t-neutral-400 pt-3'>
							<p className='font-semibold'>{CSdata.contentstackLocationConfig?.select_location?.call_center}</p>
							<p className='m-0 flex items-center'>
								<Icon
									name='phone'
									size={16}
									className='mr-2'
								/>
								<span>{CSdata.contentstackLocationConfig?.select_location?.phone}</span>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

LocationButton.propTypes = {
	className: PropTypes.string,
};

export default LocationButton;

export const query = graphql`
	query {
		contentstackLocationConfig {
			global_location {
				box_heading
				description
			}
			select_location {
				heading
				get_directions
				change_location
				current_location
				call_center
				phone
				based_on
				cta {
					...CTA
				}
			}
		}

		allShowrooms {
			nodes {
				store_number
				line_1
				line_2
				city
				state
				postal_code
				name
				branch_id
				branch_type
			}
		}
	}
`;
